/**
 * Lazy seam for `ChartRenderer` — the ONLY module in the app that reaches
 * Recharts (via `./charts`, ~260 KB minified). Charts are a deliberate
 * user action (Chart sub-tab / "Chart view" dialog), never part of first paint,
 * so every consumer (Query Editor's `ChartPanel`, Table Explorer's chart
 * dialog, the Notebook cell chart panel) renders `ChartRendererHost` instead of
 * importing `ChartRenderer` directly, which keeps Recharts in its own chunk.
 *
 * Mirrors `spatial/map/SpatialMapLazy.tsx` (the repo's first `React.lazy`),
 * including its `RandomDotmSpinner` fallback (repo rule — never `Loader2`).
 * `ChartRendererHandle` refs pass straight through the `lazy()` boundary.
 */

import { forwardRef, lazy, Suspense } from 'react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type { ChartRendererHandle, ChartRendererProps } from './ChartRenderer';

export type { ChartRendererHandle, ChartRendererProps } from './ChartRenderer';

const ChartRendererLazy = lazy(() =>
  import('./ChartRenderer').then((m) => ({ default: m.ChartRenderer })),
);

/** Suspense-wrapped host — the ergonomic entry point every chart surface renders. */
export const ChartRendererHost = forwardRef<ChartRendererHandle, ChartRendererProps>(
  function ChartRendererHost(props, ref) {
    return (
      <Suspense
        fallback={
          <div
            className="flex w-full items-center justify-center"
            style={{ height: props.height }}
            data-testid="chart-renderer-loading"
          >
            <RandomDotmSpinner size={24} />
          </div>
        }
      >
        <ChartRendererLazy {...props} ref={ref} />
      </Suspense>
    );
  },
);
